import { useEffect } from "react";
import { useRouter } from "next/router";

import { useBooleanState } from "@enkor/toss-slash";

import { ChevronLeftIcon, DotsHorizontalIcon } from "@enkor/design/icons";
import { grayScale } from "@enkor/design/colors";
import useWebStorage, { STORAGE_KEY } from "@logics/hooks/useWebStorage";
import WebViewBridge from "@logics/utils/webViewBridge";
import { NAVIGATION_ROUTE } from "@domains/common/constants/navigationRoute";
import { iconButtonAnimation } from "@domains/common/utils/animationHelper";
import { isIOSAndMac } from "@logics/utils/isMobile";

import * as S from "./Navbar.style";

interface NavbarProps {
  handleClickDotMenuButton: () => void;
}

const Navbar = ({ handleClickDotMenuButton }: NavbarProps) => {
  const { push } = useRouter();

  const [history] = useWebStorage(
    STORAGE_KEY.COMMON_HISTORY,
    [],
    "sessionStorage",
  );

  const [
    isScrolledOverNavHeight,
    setScrolledOverNavHeight,
    unsetScrolledOverNavHeight,
  ] = useBooleanState(false);

  const handleBack = (event) => {
    if (WebViewBridge.isInAppWebView()) {
      event.preventDefault();
      WebViewBridge.getInstance().navigateBack();
      return;
    }

    const lastVisitedPage = history?.[history.length - 2] as string;

    const isMyPageRoute = lastVisitedPage?.includes("/myPage");
    const isProfileEditRoute = lastVisitedPage?.includes("/profile/edit");

    if (isMyPageRoute && !isProfileEditRoute) {
      push(lastVisitedPage);
    } else {
      push(NAVIGATION_ROUTE.EVENT_LIST.HREF);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 52) {
        setScrolledOverNavHeight();
      } else {
        unsetScrolledOverNavHeight();
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const buttonStyleProps = {
    initial: { backgroundColor: grayScale[200] },
    animate: {
      backgroundColor: isScrolledOverNavHeight
        ? "rgba(0,0,0,0)" // NOTE: transparent
        : grayScale[200],
    },
  };

  return (
    <S.Header>
      <S.Navbar
        $shouldShowBackground={isScrolledOverNavHeight}
        $shouldFixSafeArea={!isIOSAndMac()}
      >
        <S.LeftButton
          type="button"
          {...buttonStyleProps}
          {...iconButtonAnimation}
          onClick={handleBack}
        >
          <ChevronLeftIcon strokeColor={grayScale.white} />
        </S.LeftButton>

        <S.RightButtonGroup>
          <S.NavRightButton
            type="button"
            {...buttonStyleProps}
            {...iconButtonAnimation}
            onClick={handleClickDotMenuButton}
          >
            <DotsHorizontalIcon strokeColor={grayScale.white} />
          </S.NavRightButton>
        </S.RightButtonGroup>
      </S.Navbar>
    </S.Header>
  );
};

export default Navbar;
